import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import './OrganizationModals.css';

const BulkUploadSalesmenModal = ({ show, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        file: null
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [uploadResult, setUploadResult] = useState(null);
    const [dragActive, setDragActive] = useState(false);

    useEffect(() => {
        if (show) {
            // Reset form when modal opens
            setFormData({ file: null });
            setError(null);
            setUploadResult(null);
        }
    }, [show]);

    const handleFileChange = (file) => {
        if (file) {
            // Validate file type
            const validTypes = [
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'text/csv'
            ];
            
            if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx?|csv)$/i)) {
                setError('Please select a valid Excel file (.xlsx, .xls, .csv)');
                return;
            }

            // Check file size (10MB limit)
            if (file.size > 10 * 1024 * 1024) {
                setError('File size must be less than 10MB');
                return;
            }

            setFormData(prev => ({ ...prev, file }));
            setError(null);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileChange(e.dataTransfer.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setUploadResult(null);

        try {
            if (!formData.file) {
                setError('Please select a file to upload');
                return;
            }

            const uploadFormData = new FormData();
            uploadFormData.append('file', formData.file);

            const response = await axiosInstance.post(
                '/api/sales-target/salesmen/bulk-upload',
                uploadFormData
            );

            if (response.data.success) {
                setUploadResult(response.data.data);
                if (response.data.data.success > 0) {
                    // Call onSuccess to refresh the parent components
                    onSuccess();
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to upload salesmen data');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setFormData({ file: null });
            setError(null);
            setUploadResult(null);
            onClose();
        }
    };

    if (!show) return null;

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-content bulk-upload-modal" onClick={(e) => e.stopPropagation()}>
                <h2>Bulk Upload Salesmen</h2>
                <p className="modal-subtitle">Upload Excel file with salesmen data</p>

                {error && <div className="error-message">{error}</div>}

                {uploadResult ? (
                    // Results Display
                    <div className="upload-results">
                        <div className="results-header">
                            <h3>Upload Results</h3>
                            <div className="results-summary">
                                <span className="success-count">✅ {uploadResult.success} Successful</span>
                                <span className="failed-count">❌ {uploadResult.failed} Failed</span>
                            </div>
                        </div>

                        {uploadResult.errors && uploadResult.errors.length > 0 && (
                            <div className="error-details">
                                <h4>Error Details:</h4>
                                <div className="error-list">
                                    {uploadResult.errors.slice(0, 10).map((error, index) => (
                                        <div key={index} className="error-item">
                                            {error}
                                        </div>
                                    ))}
                                    {uploadResult.errors.length > 10 && (
                                        <div className="error-item more-errors">
                                            ... and {uploadResult.errors.length - 10} more errors
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="modal-actions">
                            <button 
                                type="button" 
                                onClick={handleClose} 
                                className="btn-primary"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                ) : (
                    // Upload Form
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Salesmen Data File *</label>
                            <div 
                                className={`file-upload-area ${dragActive ? 'drag-active' : ''} ${formData.file ? 'has-file' : ''}`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                            >
                                <input
                                    type="file"
                                    id="file-input"
                                    accept=".xlsx,.xls,.csv"
                                    onChange={(e) => handleFileChange(e.target.files[0])}
                                    style={{ display: 'none' }}
                                    disabled={loading}
                                />
                                
                                {formData.file ? (
                                    <div className="file-selected">
                                        <div className="file-icon">📄</div>
                                        <div className="file-info">
                                            <div className="file-name">{formData.file.name}</div>
                                            <div className="file-size">
                                                {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="remove-file-btn"
                                            onClick={() => setFormData({ file: null })}
                                            disabled={loading}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ) : (
                                    <div className="file-upload-prompt">
                                        <div className="upload-icon">📁</div>
                                        <div className="upload-text">
                                            <strong>Click to select</strong> or drag and drop
                                        </div>
                                        <div className="upload-hint">
                                            Excel files (.xlsx, .xls, .csv) up to 10MB
                                        </div>
                                        <label htmlFor="file-input" className="file-select-btn">
                                            Choose File
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="info-box">
                            <h4>📋 Expected File Format:</h4>
                            <ul>
                                <li><strong>employeeId:</strong> Unique ID (e.g., B1001, EMP001)</li>
                                <li><strong>name:</strong> Full name of the salesman</li>
                                <li><strong>branchName:</strong> Exact branch name (must exist)</li>
                                <li><strong>phone:</strong> Contact number (optional)</li>
                                <li><strong>email:</strong> Email address (optional)</li>
                            </ul>
                            <p><strong>Example CSV:</strong></p>
                            <code>
                                employeeId,name,branchName,phone,email<br/>
                                B1001,John Doe,WHOLESALE,+974-12345678,john@example.com<br/>
                                B1002,Jane Smith,ALKHORE,+974-87654321,jane@example.com
                            </code>
                        </div>

                        <div className="modal-actions">
                            <button 
                                type="button" 
                                onClick={handleClose} 
                                className="btn-secondary" 
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn-primary" 
                                disabled={loading || !formData.file}
                            >
                                {loading ? (
                                    <>
                                        <span className="loading-spinner"></span>
                                        Processing...
                                    </>
                                ) : (
                                    'Upload Salesmen'
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default BulkUploadSalesmenModal;